/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { SetStateAction, useEffect, useState } from "react";
import { ArrowTrendDown, Edit, Trash, PlusCircle } from "@jda/lui-common-icon-library-mui5";
import { LuiThreeDotMenu, LuiThreeDotMenuThumbnailType } from "@jda/lui-common-component-library-mui5";
import { Box, Button, ListItemText, TextField, Tooltip, Typography, IconButton, } from "@mui/material";

import { useStyles } from "./styles";
import Loader from "../../shared/loader/Loader";
import { AlertDialog } from "../../shared/dialog/AlertDialog";
import SnackbarAlert from "../../shared/snackbar/SnackbarAlert";
import { selectLoader } from "../../store/loader/loaderSelector";
import { MESSAGES, PAGE_SIZE_ORGANIZATIONS, TITLES } from "./constants";
import { resetRealmAction } from "../../store/realm/realmActionCreator";
import Application from "../../components/applicationList/ApplicationList";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import { selectSnackBarMessage } from "../../store/snackBar/snackBarSelector";
import { actions, resources } from "../../components/checkPermission/constants";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import CustomSearchList from "../../components/customSearchList/CustomSearchList";
import { resetAccountsAction } from "../../store/subEnterprise/subEnterpriseActionCreator";
import { getOrganizations, removeOrganization } from "../../store/organization/organizations";
import AddOrganizationDialog from "../../components/addOrganizationDialog/AddOrganizationDialog";
import { setSelectedOrganizationAction } from "../../store/organization/organizationActionCreator";
import { checkAuthzPermission, CheckAuthzPermissions } from "../../components/checkPermission/CheckViewPermission";
import { selectAllOrganizations, selectNoOrganization, selectPaginationOrg, selectSelectedOrganization, } from "../../store/organization/organizationSelector";
import { isDevEnv } from '../../utility/commonUtility';
import { isIFrame } from "@jda/lui-portal-utilities";

const defaultOrganization = { id: "", name: "Organization" };

const Organization = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const isLoading = useSelector(selectLoader);
  const pagination = useSelector(selectPaginationOrg);
  const noOrganization = useSelector(selectNoOrganization);
  const snackBarMessage = useSelector(selectSnackBarMessage);
  const organizationsList = useSelector(selectAllOrganizations);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const selectedOrganizationFromStore = useSelector(selectSelectedOrganization);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [searchString, setSearchString] = useState("");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [openAddOrganization, setOpenAddOrganization] = useState(false);
  const [openSearchOrganization, setOpenSearchOrganization] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState({ id: "", name: "Organizations" });
  const isDevEnvironment = isDevEnv();

  const editOrganizationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.ORGANIZATION]);
  const deleteOrganizationPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.ORGANIZATION]);

  useEffect(() => {
    dispatch(resetRealmAction());
    dispatch(resetAccountsAction());
    getAllOrganizationList();
  }, []);

  useEffect(() => {
    if (selectedOrganizationFromStore) {
      setSelectedOrganization(
        selectedOrganizationFromStore || defaultOrganization
      );
    }
  }, [selectedOrganizationFromStore]);

  const getAllOrganizationList = async () => {
    const organizationPayload = {
      pageNo: 0,
      searchString: "",
      pageSize: PAGE_SIZE_ORGANIZATIONS,
    };
    getOrganizations(dispatch, organizationPayload);
  };

  const handleChangeOrganizationSearchInput = (event: any) => {
    const searchValue = event.target.value;
    setSearchString(searchValue);
    const organizationPayload = {
      pageNo: 0,
      searchString: searchValue,
      pageSize: PAGE_SIZE_ORGANIZATIONS,
    };
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      getOrganizations(dispatch, organizationPayload);
    }
    if (!searchValue.trim().length) {
      getAllOrganizationList();
    }
  };

  const handleGoToTheNextPage = (pageNo: number) => {
    const organizationPayload = {
      pageNo: pageNo,
      searchString: searchString,
      pageSize: PAGE_SIZE_ORGANIZATIONS,
    };
    getOrganizations(dispatch, organizationPayload);
  };

  const onSelectOrganization = (ev, value?: any) => {
    ev.stopPropagation();
    setOpenSearchOrganization(!openSearchOrganization);
    if (!value) {
      getAllOrganizationList();
    }
    if (value?.id) {
      setSelectedOrganization(value);
      dispatch(setSelectedOrganizationAction(value));
    }
  };

  const handleMenuClick = (actionName: string) => {
    if (actionName === "Edit") {
      setIsEdit(true);
      setOpenAddOrganization(true);
    } else {
      setShowDeleteAlert(true);
    }
  };

  const onAddOrganization = () => {
    setIsEdit(false);
    setOpenAddOrganization(true);
  };

  const deleteOrganization = async () => {
    await removeOrganization(dispatch, selectedOrganization?.id);
    setShowDeleteAlert(false);
  };

  const EditThreeDot = (
    <Box className={classes.menuActionWrap}>
      <Edit fontSize="small" />
      <ListItemText className={classes.menuItemText}>
        {TITLES.EDIT}
      </ListItemText>
    </Box>
  );

  const DeleteThreeDot = (
    <Box className={classes.menuActionWrap}>
      <Trash fontSize="small" />
      <ListItemText className={classes.menuItemText}>
        {TITLES.DELETE}
      </ListItemText>
    </Box>
  );

  const menuItems: LuiThreeDotMenuThumbnailType[] = [
    editOrganizationPermission && {
      autoClose: true,
      render: EditThreeDot,
      onClick: () => handleMenuClick("Edit"),
    },
    deleteOrganizationPermission && {
      autoClose: true,
      render: DeleteThreeDot,
      onClick: () => handleMenuClick("Delete"),
    },
  ];
  const iframeClass = isIFrame() ? classes.isIframe : "";
  return (
    <Box className={`${classes.organizationContainer} ${iframeClass}`} height={height}>
      <CheckAuthzPermissions action={actions.VIEW} resource={resources.ORGANIZATION}>
        <Loader showLoader={isLoading} isCenter />
        <AddOrganizationDialog
          isEdit={isEdit}
          openModal={openAddOrganization}
          refreshOnSave={getAllOrganizationList}
          organizationData={selectedOrganization}
          openCloseModal={(value: SetStateAction<boolean>) =>
            setOpenAddOrganization(value)
          }
        />
        {showDeleteAlert ? (
          <AlertDialog
            showDialogLoader={false}
            handleOnOk={deleteOrganization}
            secondButtonText={TITLES.CANCEL}
            primaryButtonText={TITLES.DELETE}
            recordName={selectedOrganization?.name}
            dialogContentText={TITLES.DELETE_ORGANIZATION}
            dialogTitle={MESSAGES.DELETE_ORGANIZATION_TITLE}
            handleOnCancel={() => setShowDeleteAlert(false)}
          />
        ) : null}
        <Box className={classes.organizationHeader}>
          <Typography className={classes.orgTitle} variant="subtitle1">
            {TITLES.ORGANIZATION}
          </Typography>
          {openSearchOrganization ? (
            <Box>
              <TextField
                autoFocus
                variant="standard"
                className={classes.organizationInput}
                placeholder={TITLES.SELECT_ORGANIZATION}
                onChange={handleChangeOrganizationSearchInput}
              />
              <CustomSearchList
                selectedItem={selectedOrganization}
                pagination={pagination}
                list={organizationsList}
                onSelectItem={onSelectOrganization}
                goToTheNextPage={handleGoToTheNextPage}
                setOpenList={setOpenSearchOrganization}
              />
            </Box>
          ) : isDevEnvironment ? (
            <Box className={classes.orgHeaderTitle}>
              <Button
                className={classes.organizationHeaderTitleWrap}
                onClick={onSelectOrganization}
              >
                <Tooltip
                  arrow
                  title={selectedOrganization?.name}
                  placement="bottom"
                >
                  <Typography className={classes.organizationHeaderText}>
                    {selectedOrganization?.name}
                  </Typography>
                </Tooltip>
                <ArrowTrendDown />
              </Button>
              <Tooltip title={TITLES.ADD_ORGANIZATION}>
                <IconButton
                  aria-label="create"
                  id="createBtn"
                  onClick={onAddOrganization}
                >
                  <PlusCircle data-testid="customers-plus-icon" />
                </IconButton>
              </Tooltip>
              {selectedOrganization.id && (editOrganizationPermission || deleteOrganizationPermission) && (
                <Box className={classes.threeDotMenu}>
                  <LuiThreeDotMenu items={_.compact(menuItems)} ariaLabel={"ThreeDotMenu"} />
                </Box>
              )}
            </Box>
          ): (<Box className={classes.orgHeaderTitle}>
                <Tooltip
                  arrow
                  title={selectedOrganization?.name}
                  placement="bottom"
                >
                  <Typography className={classes.organizationHeaderText}>
                    {selectedOrganization?.name}
                  </Typography>
                </Tooltip>
                </Box>
                )}
        </Box>
        <Box className={classes.organizationDetailsWrap}>
          {selectedOrganization?.id && <Application />}
        </Box>
        {snackBarMessage.show && (
          <SnackbarAlert
            show={snackBarMessage.show}
            message={snackBarMessage.message}
            severity={snackBarMessage.severity}
            autoHideDuration={snackBarMessage.autoHideDuration}
          />
        )}
      </CheckAuthzPermissions>
    </Box>
  );
};

export default Organization;
